import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { inr, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Reveal } from "@/components/motion";
import { StatusBadge } from "@/pages/OrderPage";

export default function Orders() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [trackId, setTrackId] = useState("");
  const [trackEmail, setTrackEmail] = useState("");
  const [trackError, setTrackError] = useState("");

  useEffect(() => {
    if (user) api.get("/orders").then((r) => setOrders(r.data)).catch(() => {});
  }, [user]);

  const track = async () => {
    setTrackError("");
    const id2 = trackEmail.trim();
    const params = id2.includes("@") ? { email: id2 } : { phone: id2 };
    try {
      const { data } = await api.get(`/orders/${trackId.trim()}`, { params });
      navigate(`/order/${data.id}?${id2.includes("@") ? `email=${encodeURIComponent(id2)}` : `phone=${encodeURIComponent(id2)}`}`);
    } catch (e) {
      setTrackError(formatApiError(e));
    }
  };

  const FIELD = "w-full border hairline bg-white px-4 py-3 text-sm focus:outline-none focus:border-[#0F5A47] transition-colors";

  return (
    <div className="max-w-[1100px] mx-auto px-5 sm:px-10 pt-28 pb-20" data-testid="orders-page">
      <Reveal>
        <p className="font-mono-ed text-[11px] uppercase tracking-[0.3em] text-[#0F5A47] mb-3">Orders</p>
        <h1 className="font-display font-extrabold tracking-tight text-4xl sm:text-5xl">Your Orders</h1>
      </Reveal>

      {user ? (
        <div className="mt-12">
          {orders.length === 0 ? (
            <p className="font-mono-ed text-xs uppercase tracking-[0.25em] text-slate-400 py-16 text-center border hairline bg-white" data-testid="orders-empty">No orders yet</p>
          ) : (
            <div className="border hairline bg-white divide-y divide-[#E2E8F0]" data-testid="orders-list">
              {orders.map((o) => (
                <Link
                  key={o.id}
                  to={`/order/${o.id}`}
                  data-testid={`order-row-${o.order_number}`}
                  className="flex flex-wrap items-center justify-between gap-3 px-6 py-5 hover:bg-[#FAFBFD] transition-colors"
                >
                  <div>
                    <p className="font-mono-ed font-semibold text-sm">{o.order_number}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{o.items.length} item(s) · {new Date(o.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</p>
                  </div>
                  <div className="flex items-center gap-5">
                    <span className="font-mono-ed text-sm font-semibold">{inr(o.total)}</span>
                    <StatusBadge status={o.status} />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      ) : (
        <Reveal delay={0.1}>
          <div className="max-w-md mt-12 border hairline bg-white p-8" data-testid="guest-track-form">
            <h2 className="font-display font-bold text-xl">Track a guest order</h2>
            <p className="text-xs text-slate-500 mt-2">Enter your order number (KR-XXXXXX) and the mobile number or email used at checkout.</p>
            <div className="space-y-3 mt-6">
              <input data-testid="track-order-id-input" className={FIELD} placeholder="Order number e.g. KR-A1B2C3" value={trackId} onChange={(e) => setTrackId(e.target.value)} />
              <input data-testid="track-email-input" className={FIELD} placeholder="Mobile number or email" value={trackEmail} onChange={(e) => setTrackEmail(e.target.value)} />
            </div>
            {trackError && <p className="text-xs text-red-600 mt-3" data-testid="track-error">{trackError}</p>}
            <button data-testid="track-order-btn" onClick={track} disabled={!trackId.trim() || !trackEmail.trim()} className="mt-6 w-full bg-[#0B0F17] text-white py-3.5 font-mono-ed text-xs uppercase tracking-[0.25em] hover:bg-[#0F5A47] transition-colors active:scale-[0.98] disabled:opacity-40">Track Order</button>
            <p className="text-xs text-slate-400 mt-4">Have an account? <Link to="/login" className="text-[#0F5A47] underline underline-offset-2">Sign in</Link> to see all orders.</p>
          </div>
        </Reveal>
      )}
    </div>
  );
}
